// ---------------------------------------------------------------------------
// TemplateReview — Approval workflow page (UI-04)
// ---------------------------------------------------------------------------

import { useParams, Link } from 'react-router';
import { ArrowLeft, Pencil } from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { useTemplate } from '@/hooks/use-templates';
import { usePersonNames } from '@/hooks/use-person-names';
import { useWorkflowHistory } from '@/hooks/use-workflow';
import { StatusBanner } from '@/components/workflow/StatusBanner';
import { TransitionButtons } from '@/components/workflow/TransitionButtons';
import { TemplatePreview } from '@/components/workflow/TemplatePreview';
import { CommentThread } from '@/components/workflow/CommentThread';
import { StatusTimeline } from '@/components/workflow/StatusTimeline';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import type { ApprovalWorkflow } from '@/types/workflow';

export default function TemplateReview() {
  const { id } = useParams();
  const { user } = useAuth();
  const { data: template, isLoading } = useTemplate(id);
  const { data: comments } = useWorkflowHistory(id);
  const { data: personNames } = usePersonNames(
    template?.created_by ? [String(template.created_by)] : [],
  );

  if (isLoading || !id) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-16" />
        <Skeleton className="h-64" />
        <Skeleton className="h-40" />
      </div>
    );
  }

  if (!template) {
    return (
      <div className="text-center py-12">
        <p className="text-carbon-500">Template not found.</p>
        <Button variant="link" asChild className="mt-2">
          <Link to="/templates">Back to Dashboard</Link>
        </Button>
      </div>
    );
  }

  // Separate transition-attached comments (workflow audit) from standalone comments
  const workflowEntries: ApprovalWorkflow[] = (comments ?? [])
    .filter((c) => c.workflow_id)
    .map((c) => ({
      workflow_id: c.workflow_id!,
      template_id: c.template_id,
      actor_id: c.author_id,
      role: c.author_role,
      from_status: template.status, // approximation — backend would return real from_status
      to_status: template.status,
      comment: c.comment_text,
      created_on: c.created_on,
    }));

  return (
    <div className="space-y-6">
      {/* Header with back + edit link */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" asChild>
          <Link to="/templates">
            <ArrowLeft className="h-5 w-5" />
          </Link>
        </Button>
        <div className="flex-1">
          <h2 className="text-xl font-semibold text-carbon-900">Template Review</h2>
          <p className="text-sm text-carbon-500">Review and approve template changes</p>
        </div>
        {template.status === 'draft' && (
          <Button variant="secondary" size="sm" asChild>
            <Link to={`/templates/${id}`}>
              <Pencil className="h-4 w-4 mr-1" /> Edit
            </Link>
          </Button>
        )}
      </div>

      {/* Status banner with transition buttons */}
      <StatusBanner
        status={template.status}
        title={template.title}
        createdBy={personNames?.get(String(template.created_by)) ?? template.created_by}
      >
        {user && (
          <TransitionButtons
            templateId={id}
            currentStatus={template.status}
            role={user.role}
          />
        )}
      </StatusBanner>

      {/* Two-column layout: preview + sidebar */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Template preview (2/3 width) */}
        <div className="lg:col-span-2 space-y-4">
          <div className="rounded-ds-008 border border-silver-200 bg-white p-5">
            <h3 className="text-sm font-semibold text-carbon-700 mb-3">Template Structure</h3>
            <TemplatePreview
              sessions={template.sessions}
              dataGroups={template.dataGroups}
            />
          </div>
        </div>

        {/* Sidebar: comments + timeline (1/3 width) */}
        <div className="space-y-6">
          <div className="rounded-ds-008 border border-silver-200 bg-white p-5">
            <CommentThread templateId={id} />
          </div>

          <div className="rounded-ds-008 border border-silver-200 bg-white p-5">
            <StatusTimeline entries={workflowEntries} />
          </div>
        </div>
      </div>
    </div>
  );
}
